<template>
    <FormField
        v-slot="$field"
        class="w-[120px]"
        :name="`fields.${field.id}`"
    >
        <AxSelect
            v-if="hasMultipleOptions"
            :modelValue="$field.value"
            optionValue="id"
            optionLabel="label"
            :label="{ value: field.label }"
            :options="fieldOptions"
            fluid
            required
            :disabled="disabled"
            :showClear="false"
            @change="handleChange"
        >
            <template #popover>
                <div class="flex w-64 flex-col gap-5">
                    <h6>
                        {{ field.label }}
                    </h6>
                    <div v-if="field.description" class="flex flex-col gap-2">
                        <p>
                            {{ t('workspace.field.description') }}
                        </p>
                        <p class="text-gray-700">
                            {{ field.description }}
                        </p>
                    </div>
                    <div class="flex flex-col gap-2">
                        <p>
                            {{ t('workspace.field.preview') }}
                        </p>
                        <p class="text-gray-700">
                            {{ $field.value }}
                        </p>
                    </div>
                </div>
            </template>
        </AxSelect>
        <AxInputText
            v-else
            :modelValue="$field.value"
            :label="{ value: field.label }"
            :maxlength="maxCharacterCount || undefined"
            :invalid="$field.touched && !!$field.error"
            :disabled="isNumAuto || disabled"
            required
            fluid
            :tooltip="forbiddenCharsTooltip"
            @update:value-model="handleValueUpdate($event, $field)"
            @input="handleInputEvent($event, $field)"
        >
            <template #popover>
                <div class="flex w-64 flex-col gap-5">
                    <div class="flex items-center gap-2">
                        <h6>
                            {{ field.label }}
                        </h6>
                        <AxTag v-if="isIndice" severity="primary" :label="$t('workspace.field.indice_tag')" />
                    </div>
                    <div v-if="isNumAuto" class="flex flex-col gap-5">
                        <p>
                            {{ t('workspace.field.num_auto') }}
                        </p>
                        <div class="flex flex-col gap-2">
                            <p>
                                {{ t('workspace.field.preview') }}
                            </p>
                            <p class="text-gray-700">
                                {{ t('workspace.field.num_auto_preview') }}
                            </p>
                        </div>
                    </div>
                    <div v-else-if="isIndice" class="flex flex-col gap-5">
                        <p>
                            {{ t('workspace.field.indice_content') }}
                        </p>
                        <div class="flex flex-col gap-2">
                            <p>
                                {{ t('workspace.field.preview') }}
                            </p>
                            <p class="text-gray-700">
                                {{ t('workspace.field.num_auto_preview') }}
                            </p>
                        </div>
                    </div>
                    <div v-else class="flex flex-col gap-5">
                        <div class="flex flex-col gap-2">
                            <p>
                                {{ t('workspace.field.character_rules.title') }}
                            </p>
                            <p class="text-gray-700">
                                {{ getCharacterRules }}
                            </p>
                        </div>
                        <div v-if="getCharacterCount" class="flex flex-col gap-2">
                            <p>
                                {{ t('workspace.field.character_count.title') }}
                            </p>
                            <p class="text-gray-700">
                                {{ getCharacterCount }}
                            </p>
                        </div>
                        <div v-if="field.description" class="flex flex-col gap-2">
                            <p>
                                {{ t('workspace.field.description') }}
                            </p>
                            <p class="text-gray-700">
                                {{ field.description }}
                            </p>
                        </div>
                        <div class="flex flex-col gap-2">
                            <p>
                                {{ t('workspace.field.preview') }}
                            </p>
                            <p class="text-gray-700">
                                {{ $field.value || `(${t('common.empty')})` }}
                            </p>
                        </div>
                    </div>
                </div>
            </template>
        </AxInputText>
    </FormField>
</template>
<script setup lang="ts">
import { computed, nextTick, ref, type PropType } from 'vue';
import { AxSelect, AxInputText, AxTag } from '@axxone-system/hades';
import { FormField } from '@primevue/forms';
import type { GetCnDataField, GetCnDataRule } from '@/shared/DTO/GED';
import { useI18n } from 'vue-i18n';
import { CN_RULE_FIELD_INPUT_TYPE } from '../constants/cnRuleField';
import { detectCharacterTypes, getMaxCharacterCount, transformCnFieldValue } from '@/shared/utils/cnHelper';

const props = defineProps({
    field: {
        type: Object as PropType<GetCnDataField>,
        required: true
    },
    rules: {
        type: Object as PropType<GetCnDataRule>,
        required: true
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false
    }
});
const { t } = useI18n();

// Track if digits were detected when only letters are allowed
const hasForbiddenDigits = ref(false);
// Track if letters were detected when only digits are allowed
const hasForbiddenLetters = ref(false);

const isNumAuto = computed(() => {
    return props.field.inputType === CN_RULE_FIELD_INPUT_TYPE.NUM_AUTO;
});

const isIndice = computed(() => {
    return props.field.isIndice;
});

const fieldOptions = computed(() => {
    const options = props.field.options || [];
    return options.map((option) => ({
        id: option,
        value: option,
        label: option
    }));
});

const hasMultipleOptions = computed(() => {
    return fieldOptions.value.length > 1;
});


const getCharacterRules = computed(() => {
    const regex = props.field.regex;
    if (!regex) {
        return '';
    }

    const { hasUppercase, hasLowercase, hasDigits, hasSpecialChars } = detectCharacterTypes(regex);
    const rules: string[] = [];

    if (hasUppercase) {
        rules.push(t('workspace.field.character_rules.uppercase'));
    }
    if (hasLowercase) {
        rules.push(t('workspace.field.character_rules.lowercase'));
    }
    if (hasDigits) {
        rules.push(t('workspace.field.character_rules.digits'));
    }
    if (hasSpecialChars) {
        rules.push(t('workspace.field.character_rules.special_chars'));
    }

    return rules.length > 0 ? rules.join(', ') : '';
});

const getCharacterCount = computed(() => {
    const regex = props.field.regex;
    if (!regex) {
        return '';
    }

    // Extract length pattern from regex
    // Look for quantifiers after character classes or groups: [abc]{3}, (abc){3,5}, etc.
    // Patterns like {3}, {3,5}, {3,}, +, *, ?

    // First, try to find quantifiers attached to character classes or groups
    // Match patterns like [abc]{3} or (abc){3,5} or .{3}
    const quantifierAfterClass = regex.match(/(?:\[[^\]]+\]|\([^)]+\)|\.)\{(\d+)(?:,(\d+)?)?\}/);
    if (quantifierAfterClass) {
        if (quantifierAfterClass[2]) {
            // Range: {3,5}
            return `${quantifierAfterClass[1]}-${quantifierAfterClass[2]}`;
        } else if (quantifierAfterClass[1]) {
            // Exact: {3} or minimum: {3,}
            const minMatch = regex.match(/(?:\[[^\]]+\]|\([^)]+\)|\.)\{(\d+),\}/);
            if (minMatch) {
                return `${quantifierAfterClass[1]}+`;
            }
            return quantifierAfterClass[1];
        }
    }

    // Fallback: look for standalone quantifiers
    const exactMatch = regex.match(/\{(\d+)\}/); // {3}
    const rangeMatch = regex.match(/\{(\d+),(\d+)\}/); // {3,5}

    if (exactMatch) {
        return exactMatch[1];
    } else if (rangeMatch) {
        return t('workspace.field.character_count.range', {
            min: rangeMatch[1], max: rangeMatch[2]
        });
    }

    return '';
});

// Computed property to get maximum character count
const maxCharacterCount = computed(() => getMaxCharacterCount(props.field.regex));

// Computed property for tooltip message (reactive)
//TODO: The component doesnt fresh when the value is changed, so the tooltip is not updated
const forbiddenCharsTooltip = computed(() => {
    const messages: string[] = [];

    if (hasForbiddenDigits.value) {
        messages.push(t('workspace.field.forbidden_chars.digits'));
    }
    if (hasForbiddenLetters.value) {
        messages.push(t('workspace.field.forbidden_chars.letters'));
    }
    if (messages.length === 0) {
        return undefined;
    }
    return messages.join('. ');
});


// Handler for @input events (native input event)
function handleInputEvent(event: Event, field: { value: string | null | undefined }) {
    const target = event.target as HTMLInputElement;
    if (!target) {
        return;
    }

    const inputValue = target.value ?? '';
    const transformedValue = transformCnFieldValue(inputValue, props.field);

    // Update forbidden char indicators for tooltip
    const { hasUppercase, hasLowercase, hasDigits } = detectCharacterTypes(props.field.regex);
    const onlyDigits = hasDigits && !hasUppercase && !hasLowercase;
    hasForbiddenDigits.value = onlyDigits ? /[^0-9]/.test(inputValue) : false;
    hasForbiddenLetters.value = !onlyDigits ? /[^a-zA-Z]/.test(inputValue) : false;

    // Update the input element value directly first for immediate visual feedback
    if (target.value !== transformedValue) {
        // Save cursor position to maintain it after value update
        const cursorPosition = target.selectionStart;
        target.value = transformedValue;

        // Restore cursor position
        nextTick(() => {
            if (cursorPosition !== null) {
                const newPosition = Math.min(cursorPosition, transformedValue.length);
                target.setSelectionRange(newPosition, newPosition);
            }
        });
    }

    // Update the field value for Vue reactivity
    field.value = transformedValue;
    emit('change');

}

// Handler for @update:value-model events (Vue model update event)
function handleValueUpdate(value: string | null | undefined, field: { value: string | null | undefined }) {
    const inputValue = value ?? '';
    const transformedValue = transformCnFieldValue(inputValue, props.field);

    // Update the field value
    if (field.value !== transformedValue) {
        field.value = transformedValue;
    }
}

function handleChange() {
    emit('change');
}

const emit = defineEmits(['change']);

</script>
